import React, { useState, useEffect } from 'react';
import {
  Stack,
  PinInput,
  Button,
  Text,
  Alert,
  Group,
  Center,
  Tooltip,
  useMantineColorScheme
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconMail,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconArrowLeft,
  IconRefresh,
  IconClock,
  IconInfoCircle
} from '@tabler/icons-react';

import { registroService } from '../../services/registroService';
import registroTemporalService from '../../services/registroTemporalService';

interface Step3EmailVerificationProps {
  email: string;
  onVerified: () => void;
  onBack: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  initialCodigo?: string;
  onCodigoChange?: (codigo: string) => void;
  useTemporalService?: boolean;
}

const Step3EmailVerification: React.FC<Step3EmailVerificationProps> = ({
  email,
  onVerified,
  onBack,
  loading,
  setLoading,
  initialCodigo,
  onCodigoChange,
  useTemporalService = false
}) => {
  const { colorScheme } = useMantineColorScheme();
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const form = useForm<{ codigo: string }>({
    initialValues: {
      codigo: initialCodigo || ''
    },
    validate: {
      codigo: (value) => {
        if (!value) return 'El código es obligatorio';
        if (value.length !== 6) return 'El código debe tener 6 dígitos';
        if (!/^\d{6}$/.test(value)) return 'El código debe contener solo números';
        return null;
      }
    }
  });

  // Countdown para reenvío
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerification = async (values: { codigo: string }) => {
    if (attempts >= maxAttempts) {
      notifications.show({
        title: 'Demasiados intentos',
        message: 'Has excedido el número máximo de intentos. Solicita un nuevo código.',
        color: 'red',
        icon: <IconX size={16} />
      });
      return;
    }

    setLoading(true);
    try {
      const result = useTemporalService 
        ? await registroTemporalService.verificarEmail({
            codigo: values.codigo,
            email: email
          })
        : await registroService.verificarEmail({
            codigo: values.codigo,
            email: email
          });

      if (result.success) {
        notifications.show({
          title: '¡Email verificado!',
          message: 'Tu correo electrónico ha sido verificado exitosamente',
          color: 'green',
          icon: <IconCheck size={16} />
        });
        onVerified();
      } else {
        setAttempts(prev => prev + 1);
        const remainingAttempts = maxAttempts - attempts - 1;
        
        notifications.show({
          title: 'Código incorrecto',
          message: result.mensaje || `Código inválido. Te quedan ${remainingAttempts} intentos.`,
          color: 'red',
          icon: <IconX size={16} />
        });
        
        form.setFieldError('codigo', 'Código incorrecto');
        form.setFieldValue('codigo', '');
      }
    } catch (error: any) {
      console.error('Error verifying email:', error);
      setAttempts(prev => prev + 1);
      
      notifications.show({
        title: 'Error de verificación',
        message: 'Hubo un problema al verificar el código. Inténtalo de nuevo.',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      const result = useTemporalService 
        ? await registroTemporalService.reenviarEmailVerificacion()
        : await registroService.reenviarEmailVerificacion(email);
      
      if (result.success) {
        notifications.show({
          title: 'Código reenviado',
          message: 'Se ha enviado un nuevo código a tu email',
          color: 'green',
          icon: <IconCheck size={16} />
        });
        
        // Reset attempts and set cooldown
        setAttempts(0);
        setResendCooldown(60); // 60 segundos
        form.setFieldValue('codigo', '');
        form.clearErrors();

        // Log para desarrollo
        if (result.tokenEmail) {
          console.log('🔐 NUEVO CÓDIGO EMAIL (DESARROLLO):', result.tokenEmail);
        }
      } else {
        notifications.show({
          title: 'Error al reenviar',
          message: result.mensaje || 'No se pudo reenviar el código',
          color: 'red',
          icon: <IconX size={16} />
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Error al reenviar el código',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setLoading(false);
    }
  };

  const formatEmail = (email: string) => {
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 3) return email;
    
    const visibleStart = localPart.substring(0, 2);
    const visibleEnd = localPart.substring(localPart.length - 1);
    const maskedPart = '*'.repeat(Math.max(1, localPart.length - 3));
    
    return `${visibleStart}${maskedPart}${visibleEnd}@${domain}`;
  };

  return (
    <form onSubmit={form.onSubmit(handleVerification)}>
      <Stack gap="xl">
        
        {/* Información del email */}
        <Alert color="blue" variant="light">
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={500}>Código enviado a:</Text>
            </Group>
            <Group gap="xs">
              <IconMail size={16} />
              <Text size="sm" ff="monospace">
                {formatEmail(email)}
              </Text>
            </Group>
          </Stack>
        </Alert>

        {/* Campo de código con PinInput */}
        <Stack gap="md">
          <Group gap="xs">
            <Text size="sm" fw={600} c="dimmed">Código de verificación</Text>
            <Tooltip
              label="El código tiene 6 dígitos, es válido por 15 minutos. Revisa tu bandeja de entrada y carpeta de spam."
              withArrow
              multiline
              w={250}
            >
              <IconInfoCircle size={14} style={{ opacity: 0.6, cursor: 'help' }} />
            </Tooltip>
          </Group>
          
          <Center>
            <PinInput
              length={6}
              type="number"
              size="xl"
              placeholder="●"
              value={form.values.codigo}
              onChange={(value) => {
                form.setFieldValue('codigo', value);
                onCodigoChange?.(value);
              }}
              error={!!form.errors.codigo}
              styles={{
                input: {
                  backgroundColor: colorScheme === 'dark'
                    ? 'rgba(30, 41, 59, 0.95)'
                    : 'rgba(247, 243, 238, 0.95)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
                  borderColor: form.errors.codigo ? 'red' : undefined,
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  borderRadius: '12px',
                  boxShadow: colorScheme === 'dark'
                    ? '0 4px 6px rgba(0, 0, 0, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                  '&:focus': {
                    borderColor: '#3b82f6',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }
                }
              }}
            />
          </Center>
          
          {form.errors.codigo && (
            <Text size="sm" c="red" ta="center">
              {form.errors.codigo}
            </Text>
          )}
        </Stack>

        {/* Estado de intentos */}
        {attempts > 0 && (
          <Alert color="orange" variant="light">
            <Group>
              <IconAlertCircle size={16} />
              <Text size="sm">
                Intentos fallidos: {attempts}/{maxAttempts}
                {attempts >= maxAttempts && ' - Solicita un nuevo código'}
              </Text>
            </Group>
          </Alert>
        )}

        {/* Botón de reenvío */}
        <Group justify="center">
          <Button
            variant="subtle"
            size="sm"
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || loading}
            leftSection={resendCooldown > 0 ? <IconClock size={14} /> : <IconRefresh size={14} />}
          >
            {resendCooldown > 0
              ? `Reenviar en ${resendCooldown}s`
              : '¿No recibiste el código? Reenviar'
            }
          </Button>
        </Group>

        {/* Botones de navegación */}
        <Group justify="space-between">
          <Button
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={onBack}
            disabled={loading}
          >
            Volver
          </Button>
          
          <Button
            type="submit"
            size="lg"
            loading={loading}
            disabled={form.values.codigo.length !== 6 || attempts >= maxAttempts}
            leftSection={<IconCheck size={16} />}
            style={{ flex: 1, marginLeft: '1rem' }}
          >
            {loading ? 'Verificando...' : 'Verificar & Continuar'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default Step3EmailVerification;